'use client';

import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';

import DashboardLayout from '@/components/layouts/DashboardLayout';
import { Card, CardContent, CardHeader, Skeleton } from '@/components/ui';
import { ADMIN_HTTP_STATUS } from '@/lib/admin/client';
import { useAdminAccess } from '@/lib/query/useAdminAccess';
import { theme } from '@/lib/ui/theme';

export function StaffPage({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const adminAccess = useAdminAccess(isLoaded && !!isSignedIn);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/sign-in', search: {} });
    }
  }, [isLoaded, isSignedIn, navigate]);

  const isLoading =
    !isLoaded ||
    (!!isSignedIn && adminAccess.data == null && !adminAccess.isError);
  if (isLoading) {
    return (
      <DashboardLayout isLoading>
        <section className="px-6 py-12 md:px-16">
          <div className="mx-auto" style={{ maxWidth: theme.spacing.maxWidth }}>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  if (
    adminAccess.data === ADMIN_HTTP_STATUS.UNAUTHORIZED ||
    adminAccess.data === ADMIN_HTTP_STATUS.FORBIDDEN
  ) {
    return (
      <DashboardLayout>
        <StaffStatusCard
          title="Staff access required"
          body="The server denied access to this internal workflow."
        />
      </DashboardLayout>
    );
  }
  if (adminAccess.data !== ADMIN_HTTP_STATUS.OK || adminAccess.isError) {
    return (
      <DashboardLayout>
        <StaffStatusCard
          title="Staff workflow unavailable"
          body="The server could not verify access to this internal workflow."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <section className="px-6 py-6 md:px-16">
        <div className="mx-auto" style={{ maxWidth: theme.spacing.maxWidth }}>
          {children}
        </div>
      </section>
    </DashboardLayout>
  );
}

function StaffStatusCard({ title, body }: { title: string; body: string }) {
  return (
    <section className="px-6 py-12 md:px-16">
      <div className="mx-auto" style={{ maxWidth: theme.spacing.maxWidth }}>
        <Card>
          <CardHeader>
            <h1 className="text-lg leading-none font-semibold">{title}</h1>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-muted-foreground text-sm">{body}</p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
